import React, { useState, useEffect } from 'react';
import { useAuth, api } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { FileText, Plus, Search, Edit, Trash2, Loader2, ArrowUpDown } from 'lucide-react';

const categories = [
  { value: 'mecanica', label: 'Mecânica' },
  { value: 'pneu', label: 'Pneu' },
  { value: 'eletrica', label: 'Elétrica' },
  { value: 'combustivel', label: 'Combustível' },
  { value: 'acidente', label: 'Acidente' },
  { value: 'seguranca', label: 'Segurança' },
  { value: 'documentacao', label: 'Documentação' },
  { value: 'carga', label: 'Carga' },
  { value: 'outros', label: 'Outros' }
];

const ReasonsPage = () => {
  const { token } = useAuth();
  const [reasons, setReasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('code_asc');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingReason, setEditingReason] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    category: 'mecanica',
    active: true
  });

  useEffect(() => {
    fetchReasons();
  }, [token]);

  const fetchReasons = async () => {
    try {
      const data = await api.get('/mission-reasons', token);
      setReasons(data);
    } catch (error) {
      toast.error('Erro ao carregar motivos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingReason) {
        await api.put(`/mission-reasons/${editingReason.id}`, formData, token);
        toast.success('Motivo atualizado');
      } else {
        await api.post('/mission-reasons', formData, token);
        toast.success('Motivo criado');
      }
      setDialogOpen(false);
      resetForm();
      fetchReasons();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este motivo?')) return;
    try {
      await api.delete(`/mission-reasons/${id}`, token);
      toast.success('Motivo removido');
      fetchReasons();
    } catch (error) {
      toast.error('Erro ao remover');
    }
  };

  const openEditDialog = (reason) => {
    setEditingReason(reason);
    setFormData({
      code: reason.code,
      description: reason.description,
      category: reason.category,
      active: reason.active
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingReason(null);
    setFormData({ code: '', description: '', category: 'mecanica', active: true });
  };

  const filteredReasons = reasons
    .filter(r =>
      r.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.description?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      if (sortBy === 'code_asc') return (a.code || '').localeCompare(b.code || '');
      if (sortBy === 'code_desc') return (b.code || '').localeCompare(a.code || '');
      if (sortBy === 'created_desc') return new Date(b.created_at) - new Date(a.created_at);
      if (sortBy === 'created_asc') return new Date(a.created_at) - new Date(b.created_at);
      return 0;
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reasons-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pl-14">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Public Sans' }}>
            Motivos de Missão
          </h1>
          <p className="text-slate-500 mt-1">{reasons.length} motivos cadastrados</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800" data-testid="add-reason-btn">
              <Plus className="w-4 h-4 mr-2" />
              Novo Motivo
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Public Sans' }}>
                {editingReason ? 'Editar Motivo' : 'Novo Motivo'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Código</Label>
                <Input value={formData.code} onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })} maxLength={5} required className="font-mono" data-testid="reason-code-input" />
              </div>
              <div className="space-y-2">
                <Label>Descrição</Label>
                <Input value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} required data-testid="reason-description-input" />
              </div>
              <div className="space-y-2">
                <Label>Categoria</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                  <SelectTrigger data-testid="reason-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={formData.active} onCheckedChange={(v) => setFormData({ ...formData, active: v })} data-testid="reason-active-switch" />
                <Label>Ativo</Label>
              </div>
              <DialogFooter>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-reason-btn">
                  {editingReason ? 'Atualizar' : 'Criar'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Buscar motivos..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9" data-testid="search-reasons-input" />
            </div>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-52" data-testid="sort-reasons">
                <ArrowUpDown className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="code_asc">Código (A-Z)</SelectItem>
                <SelectItem value="code_desc">Código (Z-A)</SelectItem>
                <SelectItem value="created_desc">Cadastro (mais recente)</SelectItem>
                <SelectItem value="created_asc">Cadastro (mais antigo)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[500px]">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Descrição</th>
                  <th>Categoria</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredReasons.map((reason) => {
                  const categoryLabel = categories.find(c => c.value === reason.category)?.label || reason.category;
                  return (
                    <tr key={reason.id} data-testid={`reason-row-${reason.id}`}>
                      <td className="font-mono font-medium text-slate-900">{reason.code}</td>
                      <td className="text-slate-600">{reason.description}</td>
                      <td className="text-slate-600">{categoryLabel}</td>
                      <td>
                        <Badge className={reason.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                          {reason.active ? 'Ativo' : 'Inativo'}
                        </Badge>
                      </td>
                      <td>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEditDialog(reason)} data-testid={`edit-reason-${reason.id}`}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(reason.id)} data-testid={`delete-reason-${reason.id}`}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
};

export default ReasonsPage;
